<template>
  <div class="app-row" :class="{ 'sequence-2': sequenceTwo, wide }">
    <slot v-if="unwrapped" />
    <AppCol v-else
      v-for="(node, i) in $slots.default?.().filter(n => !isVNodeHidden(n))"
      :key="i"
      :cols="split ? split[i] : undefined"
    >
      <component :is="node" />
    </AppCol>
  </div>
</template>

<script setup lang="ts">
import { isVNodeHidden } from '~/helpers/general';

defineProps<{
  wide?: boolean;
  sequenceTwo?: boolean;
  unwrapped?: boolean;
  split?: number[];
}>();
</script>

<style scoped lang="scss">
.app-row {
  display: flex;
  flex-wrap: wrap;
  margin: 0;

  // Replicating Vuetify's negative margins for gutters, if desired,
  // which then AppCol would counteract with padding.
  // For simplicity here, we'll manage padding directly in AppCol and override in AppRow variants.
  // If you want true gutter spacing (where padding isn't on the outer edges of the row),
  // you'd add negative margins to app-row and corresponding padding to app-col.
  // e.g. margin-left: calc(var(--space-3) * -1); margin-right: calc(var(--space-3) * -1);
}

// Styles for children when AppRow has specific props
@media (min-width: 851px) {
  .app-row.wide > .app-col {
    padding-top: var(--space-3);
    padding-bottom: var(--space-3);

    &:first-child {
      padding-inline-end: var(--space-8);
    }
    &:last-child {
      padding-inline-start: var(--space-8);
    }
  }

  .app-row.sequence-2 > .app-col,
  .app-row.sequence-2:deep(> .app-col) {
    flex-grow: 0; // Don't allow growing beyond 50%
    flex-shrink: 0; // Don't allow shrinking below 50%
    flex-basis: 50% !important; // Override inline style from `cols` if any
    max-width: 50%;   // Override inline style from `cols` if any
    padding-top: var(--space-3); // Assuming default vertical padding for sequence-2 items
    padding-bottom: var(--space-3);

    &:nth-child(odd) {
      padding-inline-start: 0;
      padding-inline-end: var(--space-3); // Default space between items
      // Original: &:last-child { padding-inline-end: var(--space-3); }
      // This means if an odd item is the last, it still gets its end padding.
      // If it's the only child (odd and last), it will have 0 start, var(--space-3) end.
    }
    &:nth-child(even) {
      padding-inline-start: var(--space-3); // Default space between items
      padding-inline-end: 0;
    }

    // Adjust padding for first/last within sequence-2 to avoid double padding or no padding at edges
    // The above nth-child rules generally handle this well for a 2-column layout.
    // If there's only one item in a sequence-2 row (e.g. filtered to one):
    &:only-child {
        padding-inline-start: 0;
        padding-inline-end: 0; // Or var(--space-3) if it should still have padding on one side
                               // Original implies it fills its 50% cell and padding adjustments are for inter-column spacing
    }
  }
}
</style>